import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api } from "@/lib/api";
import type { Broadcast, ReportSummary, SystemStatus } from "@/lib/types";
import { formatInt, formatPercent, formatShortWhen } from "@/lib/format";
import { homeSummary, recentActivity, todoList, type HomeStat, type Stat } from "@/lib/reportsApi";
import { BrandMark } from "@/components/BrandMark";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select } from "@/components/ui/select";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { ReleaseLabel } from "@/components/ReleaseLabel";
import { AttendeeBadge } from "@/features/email/EmailLabel";
import { systemLevel, type Level } from "./systemLevel";

const DOT: Record<Level | "none", string> = {
  green: "bg-[var(--color-success)]",
  yellow: "bg-[var(--color-warning)]",
  red: "bg-[var(--color-destructive)]",
  none: "bg-[var(--color-muted-foreground)]",
};

/** A green, yellow or red dot (the word next to it carries the meaning). */
export function Dot({ level, label }: { level: Level | "none"; label?: string }) {
  return <span role="img" aria-label={label ?? level} data-level={level} className={`inline-block size-2.5 shrink-0 rounded-full ${DOT[level]}`} />;
}

const link = "text-[var(--color-primary)] hover:underline";
const muted = "text-[var(--color-muted-foreground)]";
/** Money due on the home page (Brandon, 2026-11-04): green = still upcoming, red = past due, the total in normal text. */
const upcomingText = "text-[var(--color-success-fg)]";
const pastDueText = "text-[var(--color-danger-fg)]";

/** Latest sends: real campaign sends (CRM and Keap broadcasts, tests left out), or everything with tests marked. */
export const latestSendsUrl = (all: boolean) => (all ? "/api/v1/email-reports?limit=8" : "/api/v1/email-reports?limit=8&group=broadcasts&hide_tests=true");

/** Home: one-line system status, what needs Brandon, and the numbers his Keap dashboard showed. */
export function HomePage() {
  const [emailDays, setEmailDays] = useState(30);
  const [contactDays, setContactDays] = useState(30);
  const [contactTag, setContactTag] = useState(0);
  const [allSends, setAllSends] = useState(false);
  const status = useQuery({ queryKey: ["system-status"], queryFn: () => api<SystemStatus & { spool_dead_unresolved?: number }>("/api/v1/system/status"), refetchInterval: 30_000 });
  const outbox = useQuery({ queryKey: ["email-outbox"], queryFn: () => api<{ sending_paused?: boolean }>("/api/v1/email/outbox"), refetchInterval: 60_000 });
  const todo = useQuery({ queryKey: ["reports-todo"], queryFn: todoList, refetchInterval: 60_000 });
  const scheduled = useQuery({
    queryKey: ["broadcasts-home", "scheduled"],
    queryFn: () => api<{ broadcasts: Broadcast[] }>("/api/v1/broadcasts?status=scheduled&limit=20").then((r) => r.broadcasts ?? []),
  });
  const summary = useQuery({
    queryKey: ["reports-home", emailDays, contactDays, contactTag],
    queryFn: () => homeSummary({ email_days: emailDays, contacts_days: contactDays, contacts_tag_id: contactTag || undefined }),
    placeholderData: (prev) => prev,
  });
  const tags = useQuery({ queryKey: ["tags-picker"], queryFn: () => api<{ tags: { id: number; name: string }[] }>("/api/v1/tags").then((r) => r.tags ?? []), staleTime: 300_000 });
  const activity = useQuery({ queryKey: ["recent-activity"], queryFn: recentActivity, refetchInterval: 60_000 });
  const latest = useQuery({
    queryKey: ["email-reports-home", allSends],
    queryFn: () => api<{ reports: ReportSummary[] }>(latestSendsUrl(allSends)),
    placeholderData: (prev) => prev,
  });

  const s = status.data;
  const sys = s ? systemLevel(s, { sendingPaused: outbox.data?.sending_paused }) : null;
  const todoRows = (todo.data?.items ?? []).filter((i) => i.count > 0 || i.error);
  const h = summary.data;
  const upcoming = [...(scheduled.data ?? [])].sort((a, b) => String(a.scheduled_at ?? "").localeCompare(String(b.scheduled_at ?? "")));

  return (
    <div className="mx-auto w-full max-w-[1800px] space-y-3">
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-lg font-semibold">Home</h1>
        <span className={`text-sm ${muted}`}>Press ⌘K (Ctrl-K on Windows) to find anyone, or to jump to a page.</span>
      </div>

      {/* System: one line, green / yellow / red. Workers are listed on the health tab. */}
      <Card>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-sm" data-testid="system-line">
          <span className="font-medium">System</span>
          {!sys && <span className={muted}>Checking…</span>}
          {sys && (
            <span className="flex items-center gap-2" data-level={sys.level}>
              <Dot level={sys.level} label={sys.word} />
              <span>{sys.word}</span>
            </span>
          )}
          {sys && sys.reasons.length > 0 && <span className={`text-xs ${muted}`}>{sys.reasons.join(". ")}.</span>}
          <span className="ml-auto flex items-center gap-3">
            {s && <ReleaseLabel release={s.release} version={s.version} />}
            <Link to="/settings" search={{ tab: "health" }} className={`text-xs ${link}`}>
              Details
            </Link>
          </span>
        </div>
      </Card>

      {summary.isError && <p className="text-sm text-[var(--color-danger-fg)]">Couldn't load the sales numbers: {(summary.error as Error).message}</p>}
      {/* One column on phones, two on tablets, three on wide screens. */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="home-grid">
        <Card>
          <CardHeader>
            <CardTitle>To do</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm" data-testid="todo">
            {todo.isLoading && <p className={muted}>Checking…</p>}
            {todo.isError && <p className="text-[var(--color-danger-fg)]">Couldn't load the to-do list: {(todo.error as Error).message}</p>}
            {todo.data && todoRows.length === 0 && <p className={muted}>Nothing to do.</p>}
            {todoRows.map((i) => (
              <a key={i.key} href={i.link} className="flex items-center gap-2 rounded px-1 py-0.5 hover:bg-[var(--color-muted)]" title={i.error ? `Couldn't count this: ${i.error}` : undefined}>
                <span className={`num w-10 text-right font-semibold ${i.error ? "text-[var(--color-danger-fg)]" : ""}`}>{i.error ? "?" : formatInt(i.count)}</span>
                <span className="min-w-0 flex-1 truncate text-[var(--color-primary)]">{i.label}</span>
                {i.error && <span className="shrink-0 text-xs text-[var(--color-danger-fg)]">couldn't count</span>}
              </a>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Scheduled broadcasts</CardTitle>
            <Link to="/email/broadcasts" className={`text-xs ${link}`}>
              All broadcasts
            </Link>
          </CardHeader>
          <CardContent className="space-y-1 text-sm" data-testid="scheduled">
            {scheduled.isError && <p className="text-[var(--color-danger-fg)]">Couldn't load broadcasts: {(scheduled.error as Error).message}</p>}
            {scheduled.data && upcoming.length === 0 && <p className={muted}>None on the schedule.</p>}
            {upcoming.slice(0, 4).map((b) => (
              <div key={b.id} className="flex items-center gap-2">
                <BrandMark id={b.brand_id} />
                <Link to="/email/broadcasts/$broadcastId" params={{ broadcastId: String(b.id) }} className={`min-w-0 flex-1 truncate ${link}`}>
                  {b.name}
                </Link>
                {b.attendee_email && <AttendeeBadge />}
                <span className={`num whitespace-nowrap text-xs ${muted}`}>{formatShortWhen(b.scheduled_at)}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Email health</CardTitle>
            <span className={`text-xs ${muted}`}>last 30 days</span>
          </CardHeader>
          <CardContent className="space-y-1.5 text-sm" data-testid="email-health">
            {!h && <p className={muted}>Checking…</p>}
            {h && (
              <>
                <div className="flex items-center gap-2" data-level={h.email_health.level}>
                  <Dot level={h.email_health.level} label={h.email_health.summary} />
                  <span className="font-medium">{h.email_health.summary}</span>
                </div>
                {h.email_health.level !== "none" &&
                  [h.email_health.complaint_rate, h.email_health.bounce_rate, h.email_health.engagement].map((st) => (
                    <div key={st.key} className="flex items-center gap-2 text-xs">
                      <Dot level={st.level ?? "none"} label={`${st.label} ${st.level ?? ""}`} />
                      <span className="flex-1">{st.label}</span>
                      <span className="num">{st.text}</span>
                    </div>
                  ))}
                <p className={`text-xs ${muted}`}>Complaints: green under 0.08%, yellow to 0.1%.</p>
              </>
            )}
          </CardContent>
        </Card>

        <StatsCard title="Sales and receivables" stats={h?.sales} />
        <StatsCard title="Company and referral sales" stats={h?.company} />

        <Card>
          <CardHeader>
            <CardTitle>Contacts</CardTitle>
            <div className="flex items-center gap-2">
              <DaysPicker value={contactDays} onChange={setContactDays} label="New contacts period" />
              <Select aria-label="Only contacts with this tag" value={contactTag} onChange={(e) => setContactTag(Number(e.target.value))} className="h-7 max-w-44 text-xs">
                <option value={0}>Any tag</option>
                {(tags.data ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Select>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            <BigStat label={`New in the last ${contactDays} days`} stat={h?.contacts.new} />
            <BigStat label={contactTag ? `All with ${h?.contacts.tag_name ?? "this tag"}` : "All contacts"} stat={h?.contacts.total} />
          </CardContent>
        </Card>
        <Card className="md:col-span-2 xl:col-span-1">
          <CardHeader>
            <CardTitle>Marketing email</CardTitle>
            <DaysPicker value={emailDays} onChange={setEmailDays} label="Marketing email period" />
          </CardHeader>
          <CardContent className="grid grid-cols-3 gap-x-3 gap-y-2" data-testid="email-stats">
            {(h?.email.totals ?? [])
              .filter((t) => t.key !== "open_rate" && t.key !== "click_rate")
              .map((t) => {
                const rate = t.key === "opened" ? h?.email.totals.find((x) => x.key === "open_rate") : t.key === "clicked" ? h?.email.totals.find((x) => x.key === "click_rate") : undefined;
                return (
                  <div key={t.key}>
                    <div className={`text-xs ${muted}`}>{t.label}</div>
                    <div className="num text-base font-semibold">
                      {t.text}
                      {rate && <span className={`ml-1 text-xs font-normal ${muted}`}>({rate.text})</span>}
                    </div>
                  </div>
                );
              })}
          </CardContent>
        </Card>

        <Card className="min-w-0 md:col-span-2 xl:col-span-1">
          <CardHeader>
            <CardTitle>Recent activity</CardTitle>
            <span className={`text-xs ${muted}`}>Mountain Time</span>
          </CardHeader>
          <CardContent className="space-y-1 text-sm" data-testid="recent-activity">
            {activity.data && activity.data.items.length === 0 && <p className={muted}>Nothing in the last few days.</p>}
            {activity.isError && <p className="text-[var(--color-danger-fg)]">Couldn't load recent activity: {(activity.error as Error).message}</p>}
            {(activity.data?.items ?? []).map((a, i) => {
              const who = a.name || `Contact ${a.contact_id}`;
              const what = a.detail && a.kind !== "contact" ? `${a.label}: ${a.detail}` : a.label;
              // Fixed time column; the name takes at most 45% and the rest is cut with "…" (full text on hover).
              return (
                <div key={i} className="grid grid-cols-[6.25rem_minmax(0,1fr)] items-baseline gap-2" data-testid="activity-row">
                  <span className={`num whitespace-nowrap text-xs ${muted}`}>{formatShortWhen(a.at)}</span>
                  <span className="flex min-w-0 items-baseline gap-2">
                    <Link to="/contacts/$contactId" params={{ contactId: String(a.contact_id) }} className={`min-w-0 max-w-[45%] shrink-0 truncate ${link}`} title={who}>
                      {who}
                    </Link>
                    <span className="min-w-0 flex-1 truncate" title={what}>
                      {a.label}
                      {a.detail && a.kind !== "contact" ? <span className={muted}>: {a.detail}</span> : null}
                    </span>
                  </span>
                </div>
              );
            })}
          </CardContent>
        </Card>
        <Card className="min-w-0 md:col-span-2 xl:col-span-1">
          <CardHeader className="flex-wrap">
            <CardTitle>Latest sends</CardTitle>
            <span className="flex items-center gap-3">
              <label className={`flex items-center gap-1.5 text-xs ${muted}`}>
                <Checkbox checked={allSends} onChange={(e) => setAllSends(e.target.checked)} aria-label="Also show automations and notices" />
                Automations and notices too
              </label>
              <Link to="/email/reports" className={`text-xs ${link}`}>
                All email reports
              </Link>
            </span>
          </CardHeader>
          {latest.isError && <p className="px-4 py-3 text-sm text-[var(--color-danger-fg)]">Couldn't load sends: {(latest.error as Error).message}</p>}
          {(latest.data?.reports ?? []).length === 0 ? (
            <p className={`px-4 py-3 text-sm ${muted}`}>{allSends ? "No sends yet." : "No broadcasts yet."}</p>
          ) : (
            <div className="overflow-x-auto">
              <Table className="min-w-[30rem] table-fixed">
                <THead>
                  <tr>
                    <TH className="w-[7.5rem]">Sent (MT)</TH>
                    <TH>Email</TH>
                    <TH className="w-16 text-right">Sent</TH>
                    <TH className="w-16 text-right">Opened</TH>
                    <TH className="w-16 text-right">Clicked</TH>
                  </tr>
                </THead>
                <tbody>
                  {latest.data!.reports.map((r) => (
                    <TR key={r.report_unit_id}>
                      <TD className="num whitespace-nowrap">{formatShortWhen(r.first_sent_at)}</TD>
                      <TD className="min-w-0">
                        <span className="flex min-w-0 items-center gap-1.5">
                          {r.brand_id ? <BrandMark id={r.brand_id} /> : null}
                          <Link to="/email/reports/$reportId" params={{ reportId: String(r.report_unit_id) }} className={`block min-w-0 truncate ${link}`} title={r.name}>
                            {r.name}
                          </Link>
                          {r.test && (
                            <Badge variant="warning" title="Sent only to test contacts: not counted anywhere">
                              test
                            </Badge>
                          )}
                        </span>
                      </TD>
                      <TD className="num text-right">{formatInt(r.sent)}</TD>
                      <TD className="num text-right">{formatPercent(r.human_open_rate)}</TD>
                      <TD className="num text-right">{formatPercent(r.click_rate)}</TD>
                    </TR>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function DaysPicker({ value, onChange, label }: { value: number; onChange: (n: number) => void; label: string }) {
  return (
    <Select aria-label={label} value={value} onChange={(e) => onChange(Number(e.target.value))} className="h-7 w-32 shrink-0 text-xs">
      <option value={7}>Last 7 days</option>
      <option value={30}>Last 30 days</option>
      <option value={90}>Last 90 days</option>
    </Select>
  );
}

function BigStat({ label, stat }: { label: string; stat?: Stat }) {
  return (
    <div>
      <div className={`text-xs ${muted}`}>{label}</div>
      <div className="num text-2xl font-semibold">{stat ? stat.text : "…"}</div>
    </div>
  );
}

/** Keap's "Sales & Receivables Totals" style: each stat is a saved report; click to open it with its rows. */
function StatsCard({ title, stats }: { title: string; stats?: HomeStat[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <Link to="/reports" className={`text-xs ${link}`}>
          Saved reports
        </Link>
      </CardHeader>
      <CardContent className="divide-y divide-[var(--color-border)] text-sm">
        {!stats && <p className={muted}>Adding up…</p>}
        {stats?.length === 0 && <p className={muted}>No stats on the home page yet. Add one from Saved reports.</p>}
        {stats?.map((st) =>
          st.parts && st.parts.length > 0 && !st.error ? (
            <DueRow key={st.report_id} st={st} />
          ) : (
            <Link
              key={st.report_id}
              to="/reports"
              search={{ report: st.report_id }}
              className="flex items-baseline gap-3 py-1.5 hover:bg-[var(--color-muted)]"
              title={`${st.description} ${st.window_label}`}
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate">{st.name}</span>
                <span className={`block truncate text-xs ${muted}`}>{st.window_label}</span>
              </span>
              {st.error ? <span className="text-xs text-[var(--color-danger-fg)]">Couldn't add up</span> : <span className="num font-semibold">{st.stat.text}</span>}
            </Link>
          ),
        )}
      </CardContent>
    </Card>
  );
}

/** Money due (Brandon, 2026-11-04): still upcoming in green, past due in red, the total of both in normal text. Each number opens the report filtered to it. */
function DueRow({ st }: { st: HomeStat }) {
  const part = (key: string) => st.parts?.find((p) => p.key === key);
  const up = part("upcoming");
  const past = part("past_due");
  return (
    <div className="flex items-start gap-3 py-1.5" title={`${st.description} ${st.window_label}`} data-testid="due-row">
      <Link to="/reports" search={{ report: st.report_id }} className="min-w-0 flex-1 hover:underline">
        <span className="block truncate">{st.name}</span>
        <span className={`block truncate text-xs ${muted}`}>{st.window_label}</span>
      </Link>
      <span className="grid grid-cols-[auto_auto] items-baseline gap-x-2 text-right">
        <span className={`text-xs ${muted}`}>Still upcoming</span>
        <Link to="/reports" search={{ report: st.report_id, due: "upcoming" }} className={`num font-semibold hover:underline ${upcomingText}`} data-testid="due-upcoming">
          {up?.text ?? "…"}
        </Link>
        <span className={`text-xs ${muted}`}>Past due</span>
        <Link to="/reports" search={{ report: st.report_id, due: "past_due" }} className={`num font-semibold hover:underline ${pastDueText}`} data-testid="due-past">
          {past?.text ?? "…"}
        </Link>
        <span className={`text-xs ${muted}`}>Total</span>
        <Link to="/reports" search={{ report: st.report_id }} className="num font-semibold text-[var(--color-foreground)] hover:underline" data-testid="due-total">
          {st.stat.text}
        </Link>
      </span>
    </div>
  );
}
